import CtaButtonGroup from "@/components/molecules/CtaButtonGroup";
import { IdeaToIncomeProps } from "@/shared/data/home.v2.data";
import classNames from "classnames";
import SectionHeader from "./shared/SectionHeader";
import Text from "./shared/Text";

// Every card gets a subtle purple→yellow diagonal gradient.
const cardGradient =
  "bg-gradient-to-br from-[#F3EEFF] via-white to-[#FFF9E8]";

const IdeaToIncomeSection = ({
  eyebrow,
  title,
  subtitle,
  cards,
  ctas,
  columns = 2,
}: IdeaToIncomeProps) => {
  const noTimeIdx = title.toLowerCase().indexOf("in no time");
  const sentenceIdx = noTimeIdx === -1 ? title.indexOf(". ") : -1;
  let titleMuted = title;
  let titleBright = "";
  if (noTimeIdx > -1) {
    titleMuted = title.slice(0, noTimeIdx).trim();
    titleBright = title.slice(noTimeIdx);
  } else if (sentenceIdx > -1) {
    titleMuted = title.slice(0, sentenceIdx + 1);
    titleBright = title.slice(sentenceIdx + 2);
  }

  const gridColsClass =
    columns === 3 ? "md:grid-cols-3" : "md:grid-cols-2";

  return (
    <section className="w-full bg-white">
      <div className="container mx-auto px-4 py-10 sm:px-6 md:px-8 md:py-20 lg:px-12 lg:py-24">
        <SectionHeader
          eyebrow={eyebrow}
          titleMuted={titleMuted}
          titleBright={titleBright || undefined}
          title={titleBright ? undefined : title}
          subtitle={subtitle}
          variant="light-teal"
          align="center"
        />

        {ctas && ctas.length ? (
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3 md:mt-10">
            <CtaButtonGroup
              ctas={ctas}
              gap="flex gap-3"
              fullWidthOnMobile={false}
            />
          </div>
        ) : null}

        <div
          className={classNames(
            "mx-auto mt-12 grid max-w-6xl gap-6 md:items-stretch md:gap-8",
            gridColsClass
          )}
        >
          {cards.map((card) => (
            <div
              key={card.title}
              className={classNames(
                "flex h-full flex-col overflow-hidden rounded-3xl border border-[#E5E5E5] px-4 pt-4 sm:px-5 sm:pt-5 md:px-6 md:pt-6",
                cardGradient
              )}
            >
              {/* Text block TOP — eyebrow, title, description centered. */}
              <div className="text-center">
                {card.eyebrow ? (
                  <Text
                    variant="eyebrow"
                    className="text-[#CA26A5]"
                    style={{ letterSpacing: "0.6px" }}
                  >
                    {card.eyebrow}
                  </Text>
                ) : null}
                <Text
                  as="h3"
                  variant="title-lg"
                  className={classNames(
                    "text-neutralBlack",
                    card.eyebrow ? "mt-3" : ""
                  )}
                >
                  {card.title}
                </Text>
                <Text
                  as="p"
                  variant="body-lg"
                  className="mt-3 text-darkSlate04"
                >
                  {card.description}
                </Text>
              </div>
              {/* Image block BOTTOM — aspect-[4/3]; object-contain keeps
                  portrait mockups (e.g. phone) uncropped. */}
              <div className="relative mt-6 aspect-[4/3] w-full overflow-hidden rounded-2xl">
                {card.image ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={card.image}
                    alt=""
                    aria-hidden="true"
                    className={classNames(
                      "absolute inset-0 h-full w-full object-contain",
                      card.imageMobile ? "hidden md:block" : "block"
                    )}
                  />
                ) : null}
                {card.imageMobile ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={card.imageMobile}
                    alt=""
                    aria-hidden="true"
                    className={classNames(
                      "absolute inset-0 h-full w-full object-contain",
                      card.image ? "block md:hidden" : "block"
                    )}
                  />
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default IdeaToIncomeSection;
